import type { PersonSportRatingHistoryEntry } from '@court-sync/shared';
import { formatPlayerRating } from '@court-sync/shared';
import { Badge } from '@/components/ui/Badge';
import { Calendar } from 'lucide-react';

export interface PlayerRatingHistoryTimelineProps {
  entries: PersonSportRatingHistoryEntry[];
  ratingLabel?: string;
  className?: string;
}

const CHANGE_SOURCE_LABELS: Record<PersonSportRatingHistoryEntry['changeSource'], string> = {
  initial: 'Initial rating',
  manual: 'Updated',
  import: 'Imported',
  cleared: 'Cleared',
};

function formatWhen(d: Date) {
  return new Date(d).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

export function PlayerRatingHistoryTimeline({
  entries,
  ratingLabel = 'Player rating',
  className,
}: PlayerRatingHistoryTimelineProps) {
  if (entries.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No rating history yet.</p>
    );
  }

  return (
    <div className={className}>
      <ul className="space-y-3">
        {entries.map((entry) => (
          <li key={entry.id}>
            <div className="rounded-lg border border-border/80 bg-muted/20 px-3 py-2.5">
              <div className="flex flex-wrap items-start justify-between gap-2 mb-1">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4 shrink-0" aria-hidden />
                  <span>{formatWhen(entry.createdAt)}</span>
                  {entry.changedByName ? (
                    <span>· {entry.changedByName}</span>
                  ) : null}
                </div>
                <Badge variant="outline" className="text-xs font-normal">
                  {CHANGE_SOURCE_LABELS[entry.changeSource]}
                </Badge>
              </div>
              <p className="text-sm text-foreground">
                {entry.changeSource === 'cleared' ? (
                  <>
                    {ratingLabel} removed ({formatPlayerRating(entry.rating)})
                  </>
                ) : entry.previousRating != null ? (
                  <>
                    {formatPlayerRating(entry.previousRating)} → {formatPlayerRating(entry.rating)}
                  </>
                ) : (
                  <>
                    Set to {formatPlayerRating(entry.rating)}
                  </>
                )}
              </p>
              {entry.notes ? (
                <p className="mt-1 text-sm text-muted-foreground">{entry.notes}</p>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
